import React, { useState, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../store/hook"
import {
  fetchPrincipalList,
  Principal,
} from "../../features/principal/PrincipalSlice"
import {
  fetchProgramList,
  fetchProgramById,
  Program,
  fetchBankList,
  fetchProcessorList,
} from "../../features/program/ProgramSlice"
import AddPrograms from "./AddPrograms"
import ProgramList from "./ProgramList"
import EditPrograms from "./EditPrograms"
import { useNavigate } from "react-router-dom"
import { Loader2, AlertCircle, ArrowLeft, Users } from "lucide-react"

const Programs: React.FC = () => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { principalList, principalLoading, principalError } = useAppSelector(
    state => state.principal,
  )
  const {
    programList,
    programLoading,
    programError,
    selectedProgram,
    bankList,
    processorList,
  } = useAppSelector(state => state.program)

  /* --------------------------------- States ------------------------------ */

  const [showModal, setShowModal] = useState(false)
  const [showEdit, setEditModal] = useState<null | string>(null)
  const [principal, setPrincipal] = useState<null | Principal>(null)

  /* --------------------------------- Hooks ------------------------------ */

  useEffect(() => {
    if (!principal) {
      dispatch(fetchPrincipalList())
    }
  }, [])



  useEffect(() => {
    dispatch(fetchBankList())
  }, [])

  useEffect(() => {
    dispatch(fetchProcessorList())
  }, [])

  useEffect(() => {
    if (principalList?.[0]?.principalId) {
      setPrincipal({
        principalId: principalList?.[0]?.principalId,
        name: principalList?.[0]?.name,
        isDistributionOnly: principalList?.[0]?.isDistributionOnly,
      })
    }
  }, [principalList])

  useEffect(() => {
    if (principal) {
      dispatch(fetchProgramList({ principalId: principal.principalId }))
    }
  }, [principal])

  useEffect(() => {
    if (programError && showEdit) {
      const errorElement = document.getElementById("edit_ProgramError")
      errorElement?.scrollIntoView({ behavior: "smooth", block: "center" })
      errorElement?.focus()
    }
  }, [programError, showEdit])

  useEffect(() => {
    if (programError && showModal) {
      const errorElement = document.getElementById("add_ProgramError")
      errorElement?.scrollIntoView({ behavior: "smooth", block: "center" })
      errorElement?.focus()
    }
  }, [programError, showModal])

  /* ------------------------------ UI Handlers ---------------------------- */

  const renderPrincipalSelect = (readonly: boolean = false) => {
    return (
      <div className="relative">
        <select
          disabled={readonly}
          value={principal?.principalId}
          onChange={e => {
            const selectedPrincipal = principalList?.find(
              p => p.principalId.toString() === e.target.value,
            )
            if (selectedPrincipal) {
              setPrincipal({
                principalId: e.target.value,
                name: selectedPrincipal.name,
                isDistributionOnly: selectedPrincipal.isDistributionOnly,
              })
            }
          }}
          className="appearance-none pl-4 pr-10 py-2 border border-cardborder rounded-md text-sm font-medium text-gray-500 bg-white  focus:outline-none transition-colors"
        >
          {principalList?.map((row: Principal) => (
            <option key={row.principalId} value={row.principalId.toString()}>
              {row.name}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>
    )
  }

  const getContent = () => {
    let header = (
      <div className="text-2xl font-bold text-gray-900">
        Programs ({programList?.length || 0})
      </div>
    )
    let header_sidebar = null

    if (principalLoading || programLoading)
      return (
        <div className="bg-white">
          <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
            <div className="flex items-center h-10">
              {header}
            </div>
            <div className="flex items-center h-10">
              {renderPrincipalSelect()}
              {header_sidebar}
            </div>
          </div>

          <div className="flex justify-center items-center h-[calc(100vh-20rem)]">
            <div className="flex items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        </div>
      )
    else if (principalError)
      return (
        <div className="main_container">
          <div className="content_container">
            <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8 bg-white">
              <div className="flex items-center h-10">
                {header}
              </div>
              <div className="flex items-center h-10">
                {renderPrincipalSelect()}
                {header_sidebar}
              </div>
            </div>
            <div className="flex justify-center items-center min-h-[200px]">
              <div className="flex items-center gap-2 text-red-500">
                <AlertCircle className="w-5 h-5" />
                <span>{principalError || "Something went wrong!"}</span>
              </div>
            </div>
          </div>
        </div>
      )
    else if (principalList && principalList.length === 0)
      // Programs live under a principal, so without one there is nothing to
      // list or add - say so instead of rendering a blank page.
      return (
        <div className="bg-white min-h-screen">
          <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-8 py-8">
            <div className="flex items-center h-10">{header}</div>
          </div>
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex flex-col items-center justify-center text-center border border-dashed border-cardborder rounded-lg py-16 px-6">
              <Users className="w-10 h-10 text-gray-400 mb-4" />
              <h2 className="text-lg font-semibold text-gray-900">Create a principal to add programs</h2>
              <p className="text-sm text-gray-500 mt-2 max-w-md">
                Programs are created under a principal. There are no principals yet, so create one first and then come back to add programs.
              </p>
              <button
                onClick={() => navigate("/principals")}
                className="mt-6 px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:ring-1 focus:ring-brand transition-colors"
              >
                Go to Principals
              </button>
            </div>
          </div>
        </div>
      )
    else if (principal && programList && bankList) {
      let programContent = null

      if (showModal) {
        programContent = (
          <AddPrograms
            programError={programError}
            principal={principal}
            showComponent={setShowModal}
            bankList={bankList}
            processorList={processorList || []}
          />
        )
        header = (
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowModal(false)}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Add Program</h1>
          </div>
        )

        header_sidebar = (
          <div className="flex items-center gap-4">
            {renderPrincipalSelect()}
          </div>
        )
      } else if (showEdit && selectedProgram && bankList) {
        programContent = (
          <EditPrograms
            programError={programError}
            selectedProgram={selectedProgram}
            programId={showEdit}
            principal={principal}
            showComponent={setEditModal}
            bankList={bankList}
            processorList={processorList || []}
          />
        )
        header = (
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setEditModal(null)}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Edit Program</h1>
          </div>
        )

        header_sidebar = (
          <div className="flex items-center gap-4">
            {renderPrincipalSelect(true)}
          </div>
        )
      } else {
        programContent = (
          <ProgramList
            programList={programList}
            showEditModal={setEditModal}
            principalId={principal.principalId}
          />
        )
        header_sidebar = (
          <div className="flex items-center gap-3">
            {renderPrincipalSelect()}
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
            >
              Add Programs
            </button>
          </div>
        )
      }

      return (
        <div className="bg-white min-h-screen">
          <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-8 py-8">
            <div className="flex items-center h-10">
              {header}
            </div>
            <div className="flex items-center h-10">
              {header_sidebar}
            </div>
          </div>
          <div className="max-w-7xl mx-auto">
            <div className="section_container solid program">
              {programContent}
            </div>
          </div>
        </div>
      )
    }

    // Principals exist but the principal / program / bank data hasn't all
    // arrived yet - never fall through to a blank page.
    return (
      <div className="bg-white min-h-screen flex justify-center items-center">
        <div className="flex items-center gap-2">
          <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      </div>
    )
  }

  return getContent()
}

export default Programs
